import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { 
  Plus, 
  Save, 
  Trash2, 
  Edit,
  CheckCircle,
  AlertCircle,
  Eye
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../api';

// Interfaces
interface Question {
  _id?: string;
  questionText: string;
  type: 'multiple-choice' | 'true-false' | 'short-answer' | 'essay';
  options: Array<{
    _id?: string;
    text: string;
    isCorrect: boolean;
  }>;
  correctAnswer?: string;
  explanation?: string;
  points: number;
  order: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface QuizForm {
  title: string;
  description: string;
  course: string;
  section?: string;
  module?: string;
  category: 'assessment' | 'practice' | 'final' | 'certification';
  difficulty: 'facile' | 'moyen' | 'difficile';
  isActive: boolean;
  settings: {
    timeLimit: number;
    maxAttempts: number;
    passingScore: number;
    showResults: boolean;
    showCorrectAnswers: boolean;
    randomizeQuestions: boolean;
    randomizeOptions: boolean;
    allowReview: boolean;
    requireAllQuestions: boolean;
  };
}

const QuizEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [modules, setModules] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'questions' | 'settings'>('info');
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm<QuizForm>({
    defaultValues: {
      title: '',
      description: '',
      course: '',
      category: 'assessment',
      difficulty: 'moyen',
      isActive: false,
      settings: {
        timeLimit: 30,
        maxAttempts: 3,
        passingScore: 70,
        showResults: true,
        showCorrectAnswers: true,
        randomizeQuestions: false,
        randomizeOptions: false,
        allowReview: true,
        requireAllQuestions: true
      }
    }
  });

  const selectedCourse = watch('course');
  const selectedSection = watch('section');

  const loadQuiz = useCallback(async () => {
    if (!isEditing) return;
    try {
      setLoading(true);
      const response = await api.get(`/quizzes/${id}`);
      const quizData = response.data.quiz;
      
      // Populate form fields
      reset({
        title: quizData.title,
        description: quizData.description,
        course: quizData.course,
        section: quizData.section,
        module: quizData.module,
        category: quizData.category,
        difficulty: quizData.difficulty,
        isActive: quizData.isActive,
        settings: quizData.settings,
      });

      // Populate questions
      setQuestions(quizData.questions || []);
      
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error('Erreur lors du chargement du quiz.');
      navigate('/admin/quizzes');
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, navigate, reset]);

  const loadCourses = useCallback(async () => {
    try {
      const response = await api.get('/courses/list/all');
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Erreur lors du chargement des cours.');
    }
  }, []);

  const loadCourseSections = useCallback(async (courseId: string) => {
    if (!courseId) {
      setSections([]);
      setModules([]);
      setValue('section', '');
      setValue('module', '');
      return;
    }
    try {
      const response = await api.get(`/courses/${courseId}`);
      setSections(response.data.data.sections || []);
    } catch (error) {
      console.error('Erreur lors du chargement des sections');
    }
  }, [setValue]);

  const loadSectionModules = useCallback(async (courseId: string, sectionId: string) => {
    if (!sectionId) {
      setModules([]);
      setValue('module', '');
      return;
    }
    try {
      const response = await api.get(`/courses/${courseId}`);
      const section = response.data.data.sections.find((s: any) => s._id === sectionId);
      setModules(section?.modules || []);
    } catch (error) {
      console.error('Erreur lors du chargement des modules');
    }
  }, [setValue]);

  useEffect(() => {
    loadCourses();
    if (isEditing) {
      loadQuiz();
    }
  }, [isEditing, loadCourses, loadQuiz]);

  useEffect(() => {
    loadCourseSections(selectedCourse);
  }, [selectedCourse, loadCourseSections]);

  useEffect(() => {
    if (selectedCourse) {
      loadSectionModules(selectedCourse, selectedSection || '');
    }
  }, [selectedCourse, selectedSection, loadSectionModules]);

  const onSubmit = async (data: QuizForm) => {
    if (questions.length === 0) {
      toast.error('Veuillez ajouter au moins une question au quiz.');
      return;
    }

    try {
      setSaving(true);
      
      const quizData = {
        ...data,
        questions,
      };

      const url = isEditing ? `/quizzes/${id}` : '/quizzes';
      const method = isEditing ? 'put' : 'post';

      const response = await api[method](url, quizData);

      const result = response.data;
      toast.success(isEditing ? 'Quiz mis à jour avec succès !' : 'Quiz créé avec succès !');
      
      if (!isEditing) {
        navigate(`/admin/quizzes/${result.data._id}/edit`);
      }
    } catch (error: any) {
      console.error('Save quiz error:', error);
      const errorMessage = error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Erreur lors de la sauvegarde.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = (questionData: Question) => {
    const newQuestion: Question = {
      ...questionData,
      _id: Date.now().toString(), // Temporary ID
      order: questions.length + 1,
    };
    setQuestions([...questions, newQuestion]);
    setShowQuestionModal(false);
    toast.success('Question ajoutée');
  };

  const updateQuestion = (questionData: Question) => {
    setQuestions(questions.map(q => 
      q._id === editingQuestion?._id ? { ...q, ...questionData } : q
    ));
    setEditingQuestion(null);
    setShowQuestionModal(false);
    toast.success('Question mise à jour');
  };

  const deleteQuestion = (questionId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
      setQuestions(questions.filter(q => q._id !== questionId));
      toast.success('Question supprimée');
    }
  };

  const calculateTotalPoints = () => {
    return questions.reduce((total, question) => total + question.points, 0);
  };

  const moduleTypes = [
    { value: 'multiple-choice', label: 'Choix multiple', icon: CheckCircle, color: 'text-brand-600' },
    { value: 'true-false', label: 'Vrai/Faux', icon: CheckCircle, color: 'text-brand-600' },
    { value: 'short-answer', label: 'Réponse courte', icon: Edit, color: 'text-brand-600' },
    { value: 'essay', label: 'Essai', icon: Edit, color: 'text-brand-600' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Modifier le quiz' : 'Créer un nouveau quiz'}
          </h1>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/quizzes')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={saving || questions.length === 0}
              className="flex items-center px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'info', name: 'Informations générales' },
              { id: 'questions', name: `Questions (${questions.length})` },
              { id: 'settings', name: 'Paramètres' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {activeTab === 'info' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du quiz *
                  </label>
                  <input
                    {...register('title', { required: 'Le titre est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Ex: Évaluation GPS - Module 3"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-brand-600">{errors.title.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="Décrivez les objectifs et le contenu de ce quiz..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cours *
                  </label>
                  <select
                    {...register('course', { required: 'Le cours est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">Sélectionner un cours</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>{course.title}</option>
                    ))}
                  </select>
                  {errors.course && (
                    <p className="mt-1 text-sm text-brand-600">{errors.course.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Section (optionnel)
                  </label>
                  <select
                    {...register('section')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    disabled={!selectedCourse || sections.length === 0}
                  >
                    <option value="">Quiz général du cours</option>
                    {sections.map(section => (
                      <option key={section._id} value={section._id}>{section.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Module (optionnel)
                  </label>
                  <select
                    {...register('module')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    disabled={!selectedSection || modules.length === 0}
                  >
                    <option value="">Quiz de section</option>
                    {modules.map(module => (
                      <option key={module._id} value={module._id}>{module.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <select
                    {...register('category')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="assessment">Évaluation</option>
                    <option value="practice">Pratique</option>
                    <option value="final">Final</option>
                    <option value="certification">Certification</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulté
                  </label>
                  <select
                    {...register('difficulty')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="facile">Facile</option>
                    <option value="moyen">Moyen</option>
                    <option value="difficile">Difficile</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-700">
                    Activer le quiz
                  </label>
                </div>
              </div>
            </form>
          )}

          {activeTab === 'questions' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Questions du quiz</h3>
                  <p className="text-sm text-gray-600">
                    {questions.length} question(s) • {calculateTotalPoints()} points au total
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingQuestion(null);
                    setShowQuestionModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une question
                </button>
              </div>

              <div className="space-y-4">
                {questions.map((question, index) => (
                  <div key={question._id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">Question {index + 1}</span>
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {question.type === 'multiple-choice' ? 'QCM' :
                             question.type === 'true-false' ? 'Vrai/Faux' :
                             question.type === 'short-answer' ? 'Réponse courte' : 'Essai'}
                          </span>
                          <span className="px-2 py-1 text-xs bg-brand-100 text-brand-700 rounded-full">
                            {question.points} point(s)
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            question.difficulty === 'easy' ? 'bg-brand-100 text-brand-700' :
                            question.difficulty === 'medium' ? 'bg-brand-100 text-brand-700' :
                            'bg-brand-100 text-brand-700'
                          }`}>
                            {question.difficulty === 'easy' ? 'Facile' :
                             question.difficulty === 'medium' ? 'Moyen' : 'Difficile'}
                          </span>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">{question.questionText}</h4>
                        
                        {question.type === 'multiple-choice' && (
                          <div className="space-y-1">
                            {question.options.map((option, optIndex) => (
                              <div key={optIndex} className={`text-sm p-2 rounded ${
                                option.isCorrect ? 'bg-brand-50 text-brand-800 border border-brand-200' : 'text-gray-600'
                              }`}>
                                {String.fromCharCode(65 + optIndex)}. {option.text}
                                {option.isCorrect && <CheckCircle className="inline h-4 w-4 ml-2" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {question.type === 'true-false' && (
                          <div className="space-y-1">
                            {question.options.map((option, optIndex) => (
                              <div key={optIndex} className={`text-sm p-2 rounded ${
                                option.isCorrect ? 'bg-brand-50 text-brand-800 border border-brand-200' : 'text-gray-600'
                              }`}>
                                {option.text}
                                {option.isCorrect && <CheckCircle className="inline h-4 w-4 ml-2" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {question.type === 'short-answer' && (
                          <div className="text-sm text-gray-600">
                            Réponse attendue: <span className="font-medium">{question.correctAnswer}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingQuestion(question);
                            setShowQuestionModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteQuestion(question._id!)}
                          className="p-2 text-gray-400 hover:text-brand-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {question.explanation && (
                      <div className="mt-3 p-3 bg-brand-50 border border-brand-200 rounded">
                        <p className="text-sm text-brand-800">
                          <strong>Explication:</strong> {question.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                {questions.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune question créée</h3>
                    <p className="mb-4">Commencez par ajouter votre première question</p>
                    <button
                      onClick={() => {
                        setEditingQuestion(null);
                        setShowQuestionModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Créer la première question
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Paramètres du quiz</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temps limite (minutes)
                  </label>
                  <input
                    {...register('settings.timeLimit', { min: 1 })}
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tentatives maximum
                  </label>
                  <input
                    {...register('settings.maxAttempts', { min: 1, max: 10 })}
                    type="number"
                    min="1"
                    max="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Score de passage (%)
                  </label>
                  <input
                    {...register('settings.passingScore', { min: 0, max: 100 })}
                    type="number"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Options d'affichage</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      {...register('settings.showResults')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Afficher les résultats après soumission
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('settings.showCorrectAnswers')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Afficher les bonnes réponses
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('settings.randomizeQuestions')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Mélanger les questions
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('settings.randomizeOptions')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Mélanger les options de réponse
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('settings.allowReview')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Permettre la révision des réponses
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('settings.requireAllQuestions')}
                      type="checkbox"
                      className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Exiger toutes les questions
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Question Modal */}
        {showQuestionModal && (
          <QuestionModal
            onClose={() => {
              setShowQuestionModal(false);
              setEditingQuestion(null);
            }}
            onSave={editingQuestion ? updateQuestion : addQuestion}
            question={editingQuestion}
          />
        )}
      </div>
    </Layout>
  );
};

// Question Modal Component
const QuestionModal: React.FC<{
  onClose: () => void;
  onSave: (data: Question) => void;
  question?: Question | null;
}> = ({ onClose, onSave, question }) => {
  const [formData, setFormData] = useState<Partial<Question>>({
    questionText: question?.questionText || '',
    type: question?.type || 'multiple-choice',
    options: question?.options || [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ],
    correctAnswer: question?.correctAnswer || '',
    explanation: question?.explanation || '',
    points: question?.points || 1,
    difficulty: question?.difficulty || 'medium'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.questionText?.trim()) {
      toast.error('La question est requise');
      return;
    }

    if (formData.type === 'multiple-choice' || formData.type === 'true-false') {
      if (formData.options?.some(opt => !opt.text.trim())) {
        toast.error('Toutes les options doivent être remplies');
        return;
      }
      if (!formData.options?.some(opt => opt.isCorrect)) {
        toast.error('Vous devez sélectionner au moins une bonne réponse');
        return;
      }
    }

    if (formData.type === 'short-answer' && !formData.correctAnswer?.trim()) {
      toast.error('La réponse correcte est requise');
      return;
    }

    onSave(formData as Question);
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...(formData.options ?? []), { text: '', isCorrect: false }]
    });
  };

  const removeOption = (index: number) => {
    const newOptions = [...(formData.options ?? [])];
    newOptions.splice(index, 1);
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  const updateOption = (index: number, field: string, value: any) => {
    const newOptions = [...(formData.options ?? [])];
    (newOptions[index] as any)[field] = value;
    
    // Si c'est un vrai/faux, s'assurer qu'une seule option est correcte
    if (formData.type === 'true-false' && field === 'isCorrect' && value === true) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.isCorrect = false;
      });
    }
    
    setFormData({
      ...formData,
      options: newOptions
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {question ? 'Modifier la question' : 'Nouvelle question'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question *
            </label>
            <textarea
              value={formData.questionText}
              onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              placeholder="Saisissez votre question..."
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de question *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  type: e.target.value as any,
                  // Réinitialiser les options pour les types qui en ont besoin
                  options: e.target.value === 'true-false' ? 
                    [{ text: 'Vrai', isCorrect: false }, { text: 'Faux', isCorrect: false }] :
                    e.target.value === 'multiple-choice' ?
                    (formData.options?.length ?? 0) < 2 ? [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] : (formData.options ?? []) :
                    []
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="multiple-choice">Choix multiple</option>
                <option value="true-false">Vrai / Faux</option>
                <option value="short-answer">Réponse courte</option>
                <option value="essay">Essai / Développement</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points
              </label>
              <input
                type="number"
                min="1"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulté
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="easy">Facile</option>
                <option value="medium">Moyen</option>
                <option value="hard">Difficile</option>
              </select>
            </div>
          </div>

          {/* Options pour QCM */}
          {(formData.type === 'multiple-choice' || formData.type === 'true-false') && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Options de réponse *
                </label>
                {formData.type === 'multiple-choice' && (
                  <button
                    type="button"
                    onClick={addOption}
                    className="text-sm text-brand-600 hover:text-brand-700"
                  >
                    + Ajouter une option
                  </button>
                )}
              </div>
              
              <div className="space-y-3">
                {(formData.options ?? []).map((option, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <input
                      type={formData.type === 'multiple-choice' ? 'checkbox' : 'radio'}
                      checked={option.isCorrect}
                      onChange={(e) => updateOption(index, 'isCorrect', e.target.checked)}
                      className={formData.type === 'multiple-choice' ? 
                        "rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50" :
                        "border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50"
                      }
                    />
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => updateOption(index, 'text', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      placeholder={`Option ${index + 1}`}
                      required
                    />
                    {formData.type === 'multiple-choice' && (formData.options?.length ?? 0) > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-gray-400 hover:text-brand-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Réponse courte */}
          {formData.type === 'short-answer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Réponse correcte *
              </label>
              <input
                type="text"
                value={formData.correctAnswer || ''}
                onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Saisissez la réponse exacte attendue"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explication (optionnel)
            </label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              placeholder="Explication qui sera montrée après la réponse..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              {question ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuizEdit;